import { useCallback, useRef, useState } from "react";
import { AnimatePresence } from "motion/react";
import GuideStepCard from "./GuideStepCard";
import GuideStepDetail from "./GuideStepDetail";
import SimpleOverlay from "../modal/SimpleOverlay";
import Carousel from "../Carousel";
import { useLang } from "../../hooks/LangHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import animateScrollTo from "animated-scroll-to";
import Icon from "../Icon";
import GenericFlyout from "../modal/GenericFlyout";
import { cockpitComponent } from "../CockpitLayoutComponents";
import ContentSection from "../ContentSection";
import classNames from "classnames";
import { useHeightOffset } from "../../hooks/LayoutHooks";
import BjelinButton from "../BjelinButton";

export default function Guide({
  steps = [],
  selectedIndex: selectedIndexProp = 0,
  basePath = ""
}) {
  let seqIndex = 0;
  //console.log("steps:", steps);
  const stepsWithIndex = steps.map((step, i) =>
    step.component === "guide_section"
      ? {
          ...step,
          index: seqIndex++,
          childCount: step.children.length,
          children: step.children.map((ch, iCh) => ({
            ...ch,
            index: seqIndex++,
            localIndex: iCh,
            sectionIndex: i,
            fullWidth: step.settings.full_width_steps
          }))
        }
      : { ...step, index: seqIndex++, localIndex: i }
  );

  const stepsSequential = stepsWithIndex
    .reduce((res, step) => {
      return step.component === "guide_section"
        ? res
            .concat([
              {
                component: "guide_heading",
                settings: {
                  heading: step.settings.heading,
                  childCount: step.childCount
                }
              }
            ])
            .concat(step.children.map(ch => ({ ...ch })))
        : res; //To allow other components, use res.concat([{ ...step }])
    }, [])
    .map((step, i) => ({ ...step, index: i }));

  const sections = stepsWithIndex.filter(
    step => step.component === "guide_section"
  );

  //console.log("stepsWithIndex", stepsWithIndex);
  //console.log("stepsSequential", stepsSequential);

  const lang = useLang();
  const t = useBjelinTranslations();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(selectedIndexProp);
  const carouselRef = useRef();
  const [tocMenuIsOpen, setTocMenuIsOpen] = useState(false);
  const closeTocMenu = useCallback(() => {
    setTocMenuIsOpen(false);
  }, []);
  const toogleTocMenu = useCallback(() => {
    setTocMenuIsOpen(isOpen => !isOpen);
  }, []);
  const heightOffset = useHeightOffset();

  const activeSectionIndex = stepsSequential?.[selectedIndex]?.sectionIndex;
  const activeSection = sections?.[activeSectionIndex];
  const currentLocalIndex = stepsSequential?.[selectedIndex]?.localIndex;

  console.log("activeSection", activeSection, selectedIndex);

  return (
    <div className="wrapper">
      <div
        style={{
          position: "relative"
        }}
      >
        {sections.length > 1 && (
          <div className="guide-section-wrapper">
            <div className="guide-section guide-toc" id="guide-toc">
              <h2>{t("table-of-contents")}</h2>
              <ul>
                {sections.map(section => (
                  <li key={section.index}>
                    <a
                      href={`/${lang}${basePath}/${section.index + 1}`}
                      onClick={e => {
                        setSelectedIndex(section.index);
                        animateScrollTo(
                          document.getElementById(
                            `guide-section-${section.index}`
                          ),
                          { verticalOffset: -64 - heightOffset }
                        );
                        e.preventDefault();
                        return false;
                      }}
                    >
                      {section.settings.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {stepsWithIndex.map(step =>
          step.component === "guide_section" ? (
            <div className="guide-section-wrapper" key={step.index}>
              <div className="guide-section" id={`guide-section-${step.index}`}>
                <div className="guide-section-tools">
                  <BjelinButton
                    color="primary"
                    className="btn-icon btn-rounded"
                    onClick={e => {
                      setSelectedIndex(step.index);
                      setModalIsOpen(true);
                      e.preventDefault();
                      return false;
                    }}
                  >
                    <Icon name="play" type="fas" />
                  </BjelinButton>
                  <h2>{step.settings.heading}</h2>
                  <a
                    className="back-to-toc"
                    href={`/${lang}${basePath}`}
                    onClick={e => {
                      setSelectedIndex(0);
                      animateScrollTo(document.getElementById(`guide-toc`), {
                        verticalOffset: -64 - heightOffset
                      });
                      e.preventDefault();
                      return false;
                    }}
                  >
                    <Icon name="arrow-up" />
                    Tillbaka till innehållsförteckningen
                  </a>
                </div>
                <div
                  className={classNames("guide-cards", {
                    "full-width-steps": step.settings.full_width_steps
                  })}
                >
                  {step.children.map(stepInner => (
                    <GuideStepCard
                      {...stepInner.settings}
                      index={stepInner.localIndex + 1}
                      basePath={basePath}
                      fullWidth={!!stepInner.fullWidth}
                      onClick={() => {
                        setSelectedIndex(stepInner.index);
                        setModalIsOpen(true);
                      }}
                      key={stepInner.index}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <ContentSection key={step.index} theme="sand" padded>
              {step.children.map(cockpitComponent)}
            </ContentSection>
          )
        )}

        <AnimatePresence>
          {modalIsOpen && (
            <SimpleOverlay
              className="guide-overlay"
              animateScale
              close={() => {
                setModalIsOpen(false);
              }}
            >
              <div className="guide-step-carousel-menu">
                {sections.length > 1 && (
                  <div
                    className="menu-item"
                    id="guide-toc-open-button"
                    onClick={toogleTocMenu}
                  >
                    <Icon name="bars" size="lg" />
                  </div>
                )}
                <div className="menu-heading" id="guide-menu-heading">
                  {activeSection &&
                    activeSection.settings.heading +
                      " (" +
                      (currentLocalIndex + 1) +
                      " / " +
                      activeSection.childCount +
                      ")"}
                </div>
              </div>
              <Carousel
                ref={carouselRef}
                startIndex={selectedIndex}
                centeredButtons
                onSlideChange={index => {
                  setSelectedIndex(index);
                }}
                slidesData={stepsSequential}
                renderSlide={(step, i) => (
                  <GuideStepDetail
                    component={step.component}
                    {...step.settings}
                    index={step.localIndex + 1}
                    sectionStepCount={step.settings.childCount}
                    fullWidth={step.fullWidth}
                    basePath={basePath}
                    key={i}
                  />
                )}
              />
            </SimpleOverlay>
          )}
        </AnimatePresence>
        <GenericFlyout
          id="toc-flyout"
          isOpen={tocMenuIsOpen}
          close={closeTocMenu}
          excludeFromClose="guide-toc-open-button"
          align={"left"}
          fillHeight
        >
          <div className="guide-section guide-toc">
            <h2>{t("table-of-contents")}</h2>
            <ul>
              {sections.map(section => (
                <li key={section.index}>
                  <a
                    href={`/${lang}${basePath}/${section.index + 1}`}
                    onClick={e => {
                      setSelectedIndex(section.index);
                      carouselRef.current.gotoSlide(section.index);
                      setTocMenuIsOpen(false);
                      e.preventDefault();
                      return false;
                    }}
                  >
                    {section.settings.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </GenericFlyout>
      </div>
    </div>
  );
}
